/* eslint-disable multiline-ternary */
import { LOGGER_LOG_TYPE } from 'Config';
import CompaniesService from 'api/companies/CompaniesService';
import BulkImportService from 'api/bulkImport/BulkImportService';
import { BulkImportResultDto, BulkImportPreviewDto } from 'api/bulkImport/models/BulkImportResultDto';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import { useEffect, useRef, useState } from 'react';
import { Button, Col, Form, Modal, OverlayTrigger, Row, Table, Tooltip } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faDownload, faFileImport, faTimes, faTrash, faSync, faWarning } from '@fortawesome/free-solid-svg-icons';
import styles from './BulkImportScreen.module.scss';

interface IdentificationTagRow {
    code: string;
    result?: BulkImportResultDto;
}

const BulkImportIdentificationTagsTab: React.FC = () => {
    const { t } = useTranslation();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [companyId, setCompanyId] = useState<string>('');
    const [companies, setCompanies] = useState<SelectValueLabel[]>([]);
    const [rows, setRows] = useState<IdentificationTagRow[]>([]);
    const [submitted, setSubmitted] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [preview, setPreview] = useState<BulkImportPreviewDto | null>(null);
    const pendingSyncRef = useRef(false);

    useEffect(() => {
        const loadData = async () => {
            try {
                setCompanies(await CompaniesService.getCompanies());
            } catch (error) {
                Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Failed to load companies', error);
                toast.error(t('messages.error_load_info'));
            }
        };
        loadData();
    }, []);

    const handleExport = async () => {
        if (!companyId) { toast.error(t('bulkImport.select_company')); return; }
        try {
            Loading.show();
            await BulkImportService.exportIdentificationTags(companyId);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Failed to export identification tags', error);
            toast.error(t('bulkImport.export_error'));
        } finally {
            Loading.hide();
        }
    };

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target?.result as string;
            const lines = text.split('\n').filter(line => line.trim().length > 0);
            const parsed: IdentificationTagRow[] = lines.map(line => ({ code: line.trim() }));

            if (parsed.length === 0) {
                toast.error(t('bulkImport.invalid_format'));
                return;
            }

            setRows(parsed);
            setSubmitted(false);
        };
        reader.readAsText(file);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const removeRow = (index: number) => {
        setRows(prev => prev.filter((_, i) => i !== index));
    };

    const buildPayload = () => ({
        companyId,
        items: rows.map(row => ({ code: row.code.trim() }))
    });

    const handleSync = async () => {
        if (!companyId) { toast.error(t('bulkImport.select_company')); return; }
        if (rows.length === 0) { toast.error(t('bulkImport.no_data')); return; }
        for (const row of rows) {
            if (!row.code.trim()) { toast.error(t('bulkImport.validation_error')); return; }
        }

        try {
            Loading.show();
            const previewResult = await BulkImportService.previewIdentificationTags(buildPayload());
            setPreview(previewResult);
            setShowConfirmModal(true);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Identification tags preview failed', error);
            toast.error(t('bulkImport.error_message'));
        } finally {
            Loading.hide();
        }
    };

    const handleConfirmSync = () => {
        pendingSyncRef.current = true;
        setShowConfirmModal(false);
    };

    const handleModalExited = async () => {
        if (!pendingSyncRef.current) return;
        pendingSyncRef.current = false;
        try {
            Loading.show();
            const results = await BulkImportService.syncIdentificationTags(buildPayload());
            setRows(prev => prev.map((row, i) => ({ ...row, result: results.find(r => r.index === i) })));
            setSubmitted(true);
            const createdCount = results.filter(r => r.success && r.action === 'created').length;
            const errorCount = results.filter(r => !r.success).length;
            const deletedCount = preview?.toDelete?.length ?? 0;
            if (errorCount === 0) {
                toast.success(t('bulkImport.sync_success', { created: createdCount, updated: 0, deleted: deletedCount }));
            } else {
                toast(t('bulkImport.sync_partial', { created: createdCount, updated: 0, deleted: deletedCount, errors: errorCount }));
            }
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Identification tags sync failed', error);
            toast.error(t('bulkImport.error_message'));
        } finally {
            Loading.hide();
        }
    };

    const getRowClass = (row: IdentificationTagRow): string => {
        if (row.result?.success) return styles.rowSuccess;
        if (row.result && !row.result.success) return styles.rowError;
        if (!row.code.trim()) return styles.rowError;
        return '';
    };

    const pendingRows = rows.filter(r => !r.result?.success);

    return (
        <>
            <div className="bg-white rounded-2xl border shadow-sm p-4" style={{ borderColor: '#e2e8f0' }}>
                <Row className="mb-3">
                    <Col xs={12} md={6}>
                        <Form.Group controlId="idTagCompanyId">
                            <Form.Label>{t('bulkImport.select_company')}*</Form.Label>
                            <Form.Select required value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
                                <option value="">{t('common.select_option')}</option>
                                {companies.map(c => (<option key={c.value} value={c.value}>{c.label}</option>))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col xs={12} md={6} className="d-flex align-items-end">
                        <Button variant="outline-secondary" onClick={handleExport} disabled={!companyId}>
                            <FontAwesomeIcon icon={faDownload} className="me-2" />
                            {t('bulkImport.export_existing')}
                        </Button>
                    </Col>
                </Row>

                <div className={styles.uploadArea}>
                    <Form.Group controlId="idTagFileUpload">
                        <Form.Label>{t('bulkImport.upload_file')}</Form.Label>
                        <Form.Control ref={fileInputRef} type="file" accept=".txt,.csv" onChange={handleFileUpload} />
                    </Form.Group>
                </div>

                <div className="alert alert-warning d-flex align-items-center mt-3 mb-2" role="alert">
                    <FontAwesomeIcon icon={faWarning} className="me-2" />
                    {t('bulkImport.id_tags_sync_warning')}
                </div>

                <div className={styles.formatExample}>
                    <Form.Text muted>
                        <strong>{t('bulkImport.format_example')}:</strong><br />
                        <code>{t('bulkImport.id_tags_format_header')}</code>
                    </Form.Text>
                </div>

                {rows.length > 0 && (
                    <>
                        <div className={styles.tableContainer}>
                            <Table bordered hover size="sm">
                                <thead>
                                    <tr>
                                        <th style={{ width: '40px' }}>#</th>
                                        <th>{t('bulkImport.code')}</th>
                                        <th style={{ width: '90px' }}>{t('common.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row, index) => (
                                        <tr key={index} className={getRowClass(row)}>
                                            <td className="align-middle text-center">
                                                {row.result ? (
                                                    row.result.success ? (
                                                        <FontAwesomeIcon icon={faCheck} className={`${styles.statusIcon} ${styles.success}`} />
                                                    ) : (
                                                        <OverlayTrigger placement="right" overlay={<Tooltip>{row.result.error}</Tooltip>}>
                                                            <span><FontAwesomeIcon icon={faTimes} className={`${styles.statusIcon} ${styles.error}`} /></span>
                                                        </OverlayTrigger>
                                                    )
                                                ) : (index + 1)}
                                            </td>
                                            <td>
                                                <Form.Control
                                                    className={styles.inlineInput}
                                                    size="sm"
                                                    type="text"
                                                    value={row.code}
                                                    onChange={(e) => setRows(prev => { const updated = [...prev]; updated[index] = { ...updated[index], code: e.target.value }; return updated; })}
                                                    isInvalid={!row.code.trim()}
                                                    disabled={row.result?.success}
                                                />
                                            </td>
                                            <td className="text-center align-middle">
                                                {!row.result?.success && (
                                                    <Button variant="outline-danger" size="sm" onClick={() => removeRow(index)} title={t('bulkImport.remove_row') ?? ''}>
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>

                        <div className={styles.buttonsContainer}>
                            <div className={styles.summary}>
                                {submitted ? (
                                    <span>{t('bulkImport.results')}: {rows.filter(r => r.result?.success && r.result?.action === 'created').length} {t('bulkImport.created_label')} / {rows.filter(r => r.result && !r.result.success).length} {t('bulkImport.error_label')}</span>
                                ) : (
                                    <span>{rows.length} {t('bulkImport.items_count')}</span>
                                )}
                            </div>
                            {pendingRows.length > 0 && (
                                <Button variant="primary" className={styles.button} onClick={handleSync}>
                                    <FontAwesomeIcon icon={submitted ? faFileImport : faSync} className="me-2" />
                                    {submitted ? t('bulkImport.retry_failed') : t('bulkImport.sync')}
                                </Button>
                            )}
                        </div>
                    </>
                )}
            </div>

            <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} onExited={handleModalExited}>
                <Modal.Header closeButton>
                    <Modal.Title>{t('bulkImport.confirm_sync_title')}</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    {preview && (
                        <>
                            <p>{t('bulkImport.id_tags_confirm_sync_summary', { create: preview.toCreate, delete: preview.toDelete.length })}</p>
                            {preview.toDelete.length > 0 && (
                                <>
                                    <p className="text-danger fw-bold">{t('bulkImport.id_tags_confirm_delete_warning')}</p>
                                    <ul className="mb-0">
                                        {preview.toDelete.map((code, i) => (<li key={i}>{code}</li>))}
                                    </ul>
                                </>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>{t('common.cancel')}</Button>
                    <Button variant="primary" onClick={handleConfirmSync}>{t('bulkImport.confirm_sync')}</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default BulkImportIdentificationTagsTab;
